<template>
    <el-row>
        <el-col :span="24">
            <h2>Braintree Integration</h2>
        </el-col>

        <el-col :span="24" v-loading="isBusy">
            <template v-if="!isEmployerAssigned">
                <div class="alert alert-info" style="margin-top:20px">
                    Select <b>Braintree Vault Customer</b> first to get employer's payment method link.
                </div>
            </template>

            <template v-else-if="!isCustomerIdValid">
                <div class="alert alert-danger">
                    ID: '<b>{{ customerId }}</b>' was not found in Braintree Vault. Please reassign customer to
                    existing ID or create new customer in Vault.
                </div>
            </template>

            <template v-else>
                <div class="alert alert-info" style="margin-top:20px">
                    Actual assigned customer: <b>{{customer.company}}</b>
                    (<a :href="`mailto:${customer.email}`">{{customer.email}}</a>)
                    <el-button type="default"
                               style="margin:0 10px"
                               @click="changingCustomer = true">Change Customer
                    </el-button>
                </div>

                <div class="alert alert-success" style="margin-top:20px" v-if="!changingCustomer">
                    <b>Employer link for payment gate:</b><br>
                    {{ employerPaymentGateLink }}
                    <el-button type="default" style="margin-left:20px" @click="copyToClipboard">
                        <i class="fa fa-copy"></i> Copy link
                    </el-button>
                </div>
            </template>

            <el-form style="margin-top:20px" v-if="changingCustomer || !isEmployerAssigned || !isCustomerIdValid">
                <el-form-item label="Select Braintree Vault Customer">
                    <el-select v-model="customer.id"
                               filterable
                               remote
                               :loading="customerLoading"
                               no-data-text=""
                               :remote-method="getCustomerList"
                               placeholder="Type at least 3 characters to start search in company name or email"
                               style="width:40%">
                        <el-option v-for="customer of customerList"
                                   :key="customer.id"
                                   :label="customer.company"
                                   :value="customer.id">
                            <span style="float:left">{{ customer.company }}</span>
                            <span style="float:right;color:#8492a6;font-size:13px;padding-left:15px;">{{ customer.email }}</span>
                        </el-option>
                    </el-select>
                    <el-button type="primary" @click="saveCustomerIdToEmployer">Save</el-button>
                    <el-button type="default" @click="changingCustomer = false" v-if="changingCustomer">Cancel
                    </el-button>
                    <span style="padding-left:10px">
                        or <a href="#"
                              @click.prevent="createVaultCustomerDialogVisible = true">create a new customer</a>
                    </span>
                </el-form-item>
            </el-form>

            <CreateVaultCustomerForm :show-form="createVaultCustomerDialogVisible"
                                     :employer-id="employerId"
                                     :employer-name="employerName"
                                     :employer-email="employerEmail"
                                     @is-visible="createVaultCustomerDialogVisible = $event"
                                     @created="customerCreated($event)"/>
        </el-col>
    </el-row>
</template>

<script>
import CreateVaultCustomerForm from './forms/create-vault-customer';

export default {
        components: { CreateVaultCustomerForm },
        props: {
            employerId: { type: Number, required: true },
            employerName: { type: String, required: true },
            employerEmail: { type: String, required: true },
            customerId: { type: String, required: true },
            linkToken: { type: String, required: true },
            customerCompany: { type: String, default: null },
            customerEmail: { type: String, default: null },
            customerIdIsValid: { type: Boolean, required: true },
        },
        data() {
            return {
                isBusy: false,
                customerList: !this.customerId || !this.customerIdIsValid ? [] : [
                    {
                        id: this.customerId,
                        company: this.customerCompany,
                        email: this.customerEmail,
                    }],
                createVaultCustomerDialogVisible: false,
                changingCustomer: false,
                isCustomerIdValid: this.customerIdIsValid,
                customer: {
                    id: this.customerId == null || this.customerId.trim() === '' ? null : this.customerId,
                    token: this.linkToken == null || this.linkToken.trim() === '' ? null : this.linkToken,
                    company: this.customerCompany == null ? '' : this.customerCompany,
                    email: this.customerEmail == null ? '' : this.customerEmail,
                },
                customerLoading: false,
            };
        },
        computed: {
            isEmployerAssigned() {
                return this.customer.id !== null && this.customer.token !== null;
            },
            employerPaymentGateLink() {
                return this.url(`payment-gate/${this.customer.token}`);
            },
        },
        methods: {
            customerCreated(data) {
                this.customer.id = data.customerId;
                this.customer.token = data.customerToken;
                this.customer.company = data.customerCompany;
                this.customer.email = data.customerEmail;

                this.customerList = [this.customer];
                this.isCustomerIdValid = true;
            },
            getCustomerList(query) {
                if (query.trim() === '' || query.length < 3) {
                    this.customerList = [];
                    return;
                }

                const $this = this;
                $this.customerLoading = true;
                axios.get(
                    url(`admin/employer/${$this.employerId}/braintree/get-customer-list?query=${encodeURIComponent(
                        query)}`))
                     .then(response => {
                         if (typeof response.data.customerList === 'undefined') {
                             throw Error('Customer list could not be retreived from Braintree.');
                         }
                         $this.customerList = response.data.customerList;
                     })
                     .catch(err => $this.$message.error(err.message))
                     .finally(() => $this.customerLoading = false);
            },
            saveCustomerIdToEmployer() {
                this.isBusy = true;

                const actualCustomer = this.customerList.filter(customer => {
                    return customer.id === this.customer.id;
                });

                const $this = this;
                axios.post(url(`admin/employer/${$this.employerId}/braintree/save-customer`),
                    { id: $this.customer.id, company: actualCustomer[0].company, email: actualCustomer[0].email })
                     .then(response => {
                         const data = response.data;
                         if (typeof data.id !== 'undefined' && typeof data.token !== 'undefined') {
                             $this.customer.id = data.id;
                             $this.customer.token = data.token;
                             $this.customer.company = actualCustomer[0].company;
                             $this.customer.email = actualCustomer[0].email;
                             $this.changingCustomer = false;
                             $this.isCustomerIdValid = true;
                         }
                     })
                     .catch(err => this.$message.error(err.message))
                     .finally(() => this.isBusy = false);
            },
            copyToClipboard() {
                // first create invisible (can't be hidden, otherwise text will not be copied!) text area with link text
                const linkObj = document.createElement('textarea');
                linkObj.value = this.employerPaymentGateLink;
                linkObj.style.cssText = 'position:fixed;pointer-events:none;z-index:-9999;opacity:0;';
                linkObj.setAttribute('readonly', 'readonly');
                document.body.appendChild(linkObj);

                // copy text into clipboard
                linkObj.select();
                linkObj.setSelectionRange(0, 99999);// this is hack for mobile phones
                document.execCommand('copy');

                // deselect and remove text area from document
                window.getSelection().removeAllRanges();
                document.body.removeChild(linkObj);

                // show message
                this.$message.success('Copied!');
            },
        },
    };
</script>

<style>
    .el-form-item__label {
        padding-top: 10px;
    }
</style>